import React, { Dispatch, SetStateAction, useState } from 'react';
import Modal from '../modal/Modal';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import { MemberMembershipDataType } from '../../services/useMembershipQueryService';
import { Body2Bold } from '../../lib/font';
import { useTickets } from '../../features/groupTicket/hooks';
import { Ticket } from '../../features/groupTicket/types';
import { convertMembershipToGroupTicket } from '../../api/memberShip';
import { useQueryClient } from '@tanstack/react-query';
import { groupTicketKeys } from '../../features/groupTicket/keys';
import GroupTicketPicker from './GroupTicketPicker';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    membership: MemberMembershipDataType;
    memberName: string;
    centerId: string;
    memberId: string;
    createAlert: any;
};

const ModalConvertToGroupTicket = ({
    setIsVisible,
    membership,
    memberName,
    centerId,
    memberId,
    createAlert,
}: Props) => {
    const queryClient = useQueryClient();
    const authorization = getItemWithExpiration('authorization');

    const [selectedTicketId, setSelectedTicketId] = useState<string>('');
    const [memo, setMemo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { data: tickets = [] } = useTickets({ centerId, enabled: !!centerId });
    const activeTickets = (tickets as Ticket[]).filter((t) => t.isActive);
    const selectedTicket = activeTickets.find((t) => t._id === selectedTicketId);

    const remainSession = membership?.remainSession ?? 0;
    const perSessionFee = membership?.pricePerSchedule ?? 0;

    const handleConfirm = async () => {
        if (!selectedTicketId) {
            setErrorMessage('그룹 수강권 상품을 선택해주세요.');
            return;
        }
        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const res = await convertMembershipToGroupTicket({
                centerId,
                membershipId: membership._id,
                ticketId: selectedTicketId,
                memo: memo || undefined,
                accessToken: authorization,
            });

            if (res?.status === 201) {
                // 양쪽 쿼리 캐시 무효화
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterMemberList'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
                    queryClient.invalidateQueries({ queryKey: ['getTrainerMemberCountAPI'] }),
                    queryClient.invalidateQueries({ queryKey: ['getSalesListQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getUnpaidPaymentsQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] }),
                    queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleFeeQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleSessionQuery'] }),
                    queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] }),
                    queryClient.invalidateQueries({ queryKey: groupTicketKeys.memberList(centerId, memberId) }),
                    queryClient.invalidateQueries({ queryKey: groupTicketKeys.all }),
                ]);

                createAlert('전환 완료', 'PT 수강권이 그룹 수강권으로 전환되었습니다.', false);
                setIsVisible(false);
                // 뒤로가기 (만료된 수강권 상세 페이지에서 빠져나감)
                window.history.back();
            } else {
                setErrorMessage(res?.message || '전환에 실패했습니다.');
            }
        } catch (err: any) {
            setErrorMessage(err?.message || '전환 중 오류가 발생했습니다.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal setIsVisible={setIsVisible} whiteViewWidth={640} isCloseButton mt={100} mb={100}>
            <Col className="p-[32px]">
                {/* 헤더 */}
                <Body2Bold className="mb-[8px]">PT 수강권 → 그룹 수강권 전환</Body2Bold>

                {/* 안내 문구 */}
                <Col className="bg-[#FFF8E1] rounded-[8px] p-[12px] mb-[20px]">
                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.2} color="#92700C">
                        기존에 1:1 PT 수강권으로 2:1 수업을 진행하셨나요?{'\n'}
                        이 기능을 사용하면 남은 횟수를 그룹 수강권으로 전환하여{'\n'}
                        그룹수업 기능으로 편리하게 관리할 수 있습니다.
                    </DemiLightText>
                    <DemiLightText fontSize={12} lineHeight={16} letterSpacing={-0.2} color="#B8960C" className="mt-[6px]">
                        * 기존 수강권은 만료 처리되며, 이미 완료된 수업과 매출 기록은 유지됩니다.
                    </DemiLightText>
                </Col>

                {/* 원본 수강권 요약 */}
                <Col className="bg-[#F4F5F7] rounded-[8px] p-[16px] mb-[20px]">
                    <MediumText fontSize={14} lineHeight={20} letterSpacing={-0.3} color={colors.LAVEL_2}>
                        기존 PT 수강권
                    </MediumText>
                    <Col className="mt-[8px] gap-[4px]">
                        <InfoRow label="수강권명" value={membership?.name || '-'} />
                        <InfoRow label="잔여 횟수" value={`${remainSession}회`} />
                        <InfoRow label="회당 금액" value={convertToKRW(perSessionFee)} />
                        <InfoRow label="만료일" value={membership?.expirationDate?.slice(0, 10) || '-'} />
                    </Col>
                </Col>

                {/* 그룹 수강권 상품 선택 — 이름만 나열하면 8대1인지 1:2인지, 몇 회짜린지 구분이 안 된다.
                    수강권 카드와 같은 모양으로 그려 눈으로 고르게 한다. */}
                <Col className="mb-[16px]">
                    <Row className="mb-[8px] items-baseline justify-between">
                        <MediumText fontSize={14} lineHeight={20} letterSpacing={-0.3} color={colors.LAVEL_2}>
                            전환할 그룹 수강권 상품
                        </MediumText>
                        <RegularText fontSize={12} lineHeight={18} letterSpacing={-0.2} color="#98A2B3">
                            {activeTickets.length > 0 ? `${activeTickets.length}개` : ''}
                        </RegularText>
                    </Row>

                    <GroupTicketPicker
                        tickets={activeTickets}
                        selectedTicketId={selectedTicketId}
                        onSelect={(ticketId) => {
                            setSelectedTicketId(ticketId);
                            setErrorMessage('');
                        }}
                    />
                </Col>

                {/* 전환 결과 미리보기 */}
                {selectedTicket && (
                    <Col className="bg-[#EEF4FF] rounded-[8px] p-[16px] mb-[16px]">
                        <MediumText fontSize={14} lineHeight={20} letterSpacing={-0.3} color="#2563EB">
                            전환 결과 미리보기
                        </MediumText>
                        <Col className="mt-[8px] gap-[4px]">
                            <InfoRow label="상품명" value={selectedTicket.name} />
                            <InfoRow label="잔여 횟수" value={`${remainSession}회`} />
                            <InfoRow label="회당 금액" value={convertToKRW(perSessionFee)} />
                            <InfoRow label="판매 금액" value="0원 (이미 결제 완료)" />
                        </Col>
                        <DemiLightText fontSize={12} lineHeight={16} letterSpacing={-0.2} color="#6B7280" className="mt-[8px]">
                            * 매출 정산에 중복 반영되지 않습니다
                        </DemiLightText>
                    </Col>
                )}

                {/* 메모 */}
                <Col className="mb-[20px]">
                    <MediumText fontSize={14} lineHeight={20} letterSpacing={-0.3} color={colors.LAVEL_2} className="mb-[8px]">
                        메모 (선택)
                    </MediumText>
                    <textarea
                        className="w-full h-[72px] border border-[#E0E0E0] rounded-[8px] p-[12px] text-[14px] resize-none"
                        placeholder="전환 관련 메모를 입력하세요"
                        value={memo}
                        onChange={(e) => setMemo(e.target.value)}
                    />
                </Col>

                {/* 에러 메시지 */}
                {errorMessage && (
                    <RegularText fontSize={13} lineHeight={18} letterSpacing={-0.2} color="#FF2020" className="mb-[12px]">
                        {errorMessage}
                    </RegularText>
                )}

                {/* 버튼 */}
                <Row className="gap-[8px] justify-end">
                    <Col
                        className="w-[120px] h-[48px] bg-[#F4F5F7] justify-center items-center rounded-[8px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.3} color={colors.LAVEL_2}>
                            취소
                        </RegularText>
                    </Col>
                    <Col
                        className={`w-[120px] h-[48px] justify-center items-center rounded-[8px] cursor-pointer ${
                            isSubmitting || !selectedTicketId ? 'bg-[#D1D5DB]' : 'bg-[#2563EB]'
                        }`}
                        onClick={() => {
                            if (!isSubmitting && selectedTicketId) handleConfirm();
                        }}
                    >
                        <RegularText fontSize={16} lineHeight={24} letterSpacing={-0.3} color="#FFFFFF">
                            {isSubmitting ? '전환 중...' : '전환하기'}
                        </RegularText>
                    </Col>
                </Row>
            </Col>
        </Modal>
    );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <Row className="justify-between">
        <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.2} color="#6B7280">
            {label}
        </DemiLightText>
        <RegularText fontSize={13} lineHeight={18} letterSpacing={-0.2} color={colors.LAVEL_1}>
            {value}
        </RegularText>
    </Row>
);

export default ModalConvertToGroupTicket;
